import {TreeView} from '@primer/react'
import type {TraceNode} from './types'
import {TimingDetail as DurationDetail} from './NameWithDetails'
import styles from './PerformancePaneItem.module.css'

type TimingContextProps = {
  idPrefix: string
  timingContext: TraceNode
}

type TimingDetail = {
  key: string
  label: string
  value?: number
  children?: TimingDetail[]
}

const tracerMeasurementLabels: Record<string, string> = {
  graphql_duration_ms: 'GraphQL execution',
  platform_setup_duration_ms: 'Platform setup',
  platform_teardown_duration_ms: 'Platform teardown',
}

const browserMeasurementLabels: Record<string, string> = {
  browser_queueing_ms: 'Browser queueing',
  browser_connection_stalled_ms: 'Connection stalled',
  browser_waiting_for_server_response_ms: 'Waiting for server response',
  browser_content_download_ms: 'Content download',
  browser_dns_lookup_ms: 'DNS lookup',
  browser_tcp_connection_ms: 'TCP connection',
  browser_tls_negotiation_ms: 'TLS negotiation',
}

const serverTimingLabels: Record<string, string> = {
  Rails: 'Rails request',
  'Rails-Before-Action': 'Before Rails action',
  'Rails-Action': 'Rails action',
  'Rails-After-Action': 'After Rails action',
  'GraphQL-Render': 'Response serialization',
  Unicorn: 'Web server response',
  GLB: 'GLB → nginx',
  Nginx: 'Nginx queue time',
}

export function TimingContext({idPrefix, timingContext}: TimingContextProps) {
  const tracerDetails = getKnownDetails(timingContext, tracerMeasurementLabels)
  const tracerDuration = getKnownDetail(timingContext, 'duration_ms', 'Tracer duration')
  const tracerNode = createTimingNode(tracerDuration, 'tracer-duration', 'Tracer duration', tracerDetails)

  const platformExecuteCall = getKnownDetail(
    timingContext,
    'platform_execute_call_duration_ms',
    'Platform.execute call',
  )
  const platformExecuteChildren = [
    getKnownDetail(timingContext, 'platform_execute_before_tracer_ms', 'Before tracer'),
    tracerNode,
    getKnownDetail(timingContext, 'platform_execute_after_tracer_ms', 'After tracer'),
  ].filter((detail): detail is TimingDetail => detail !== undefined)
  const platformExecuteNode = createTimingNode(
    platformExecuteCall,
    'platform-execute-call',
    'Platform.execute call',
    platformExecuteChildren,
  )

  const controllerChildren = [
    getKnownDetail(timingContext, 'controller_before_platform_execute_ms', 'Controller before Platform.execute'),
    platformExecuteNode,
    getKnownDetail(
      timingContext,
      'controller_after_platform_execute_before_trace_serialization_ms',
      'Post query execution',
    ),
  ].filter((detail): detail is TimingDetail => detail !== undefined)

  const controllerTotal = getKnownDetail(
    timingContext,
    'controller_elapsed_before_trace_serialization_ms',
    'Controller total elapsed time',
  )
  const controllerNode = createTimingNode(
    controllerTotal,
    'controller-total',
    'Controller total elapsed time',
    controllerChildren,
  )

  const browserRequest = createTimingNode(
    getKnownDetail(timingContext, 'browser_resource_timing_duration_ms', 'Browser request'),
    'browser-request',
    'Browser request',
    getKnownDetails(timingContext, browserMeasurementLabels),
  )

  const serverTimingDetails = getServerTimingDetails(timingContext.server_timing_ms)
  const responseSerialization = takeTimingDetail(serverTimingDetails, 'GraphQL-Render')
  const railsActionChildren = controllerNode ? [controllerNode] : []
  const railsAction = nestTimingDetail(takeTimingDetail(serverTimingDetails, 'Rails-Action'), railsActionChildren)
  const railsRequestChildren = [
    takeTimingDetail(serverTimingDetails, 'Rails-Before-Action'),
    ...railsAction,
    takeTimingDetail(serverTimingDetails, 'Rails-After-Action'),
  ].filter((detail): detail is TimingDetail => detail !== undefined)
  let serverRequestChildren = [
    ...nestTimingDetail(takeTimingDetail(serverTimingDetails, 'Rails'), railsRequestChildren),
    responseSerialization,
  ].filter((detail): detail is TimingDetail => detail !== undefined)
  serverRequestChildren = nestTimingDetail(takeTimingDetail(serverTimingDetails, 'Unicorn'), serverRequestChildren)
  const serverRequest = createTimingNode(undefined, 'server-request', 'Server request', serverRequestChildren)

  const upstreamQueueing = createTimingNode(
    undefined,
    'upstream-queueing',
    'Upstream queueing',
    takeTimingDetails(serverTimingDetails, ['GLB', 'Nginx']),
  )

  const timingDetails = [browserRequest, serverRequest, upstreamQueueing].filter(
    (detail): detail is TimingDetail => detail !== undefined,
  )

  if (timingDetails.length === 0) return null

  return (
    <TreeView.Item id={`${idPrefix}-timing-context`}>
      Timing details
      <TreeView.SubTree>
        {timingDetails.map(detail => (
          <TimingContextItem key={detail.key} idPrefix={`${idPrefix}-timing`} detail={detail} />
        ))}
      </TreeView.SubTree>
    </TreeView.Item>
  )
}

function TimingContextItem({idPrefix, detail}: {idPrefix: string; detail: TimingDetail}) {
  return (
    <TreeView.Item id={`${idPrefix}-${detail.key}`}>
      <TimingContextDetail detail={detail} />
      {detail.children && detail.children.length > 0 && (
        <TreeView.SubTree>
          {detail.children.map(child => (
            <TimingContextItem key={child.key} idPrefix={`${idPrefix}-${detail.key}`} detail={child} />
          ))}
        </TreeView.SubTree>
      )}
    </TreeView.Item>
  )
}

function TimingContextDetail({detail}: {detail: TimingDetail}) {
  return (
    <div className={styles.PerformancePaneItemDetailsContainer}>
      <span className={styles.PerformancePaneItemName}>{detail.label}</span>
      {detail.value === undefined ? null : <DurationDetail duration={detail.value} />}
    </div>
  )
}

function getKnownDetail(timingContext: TraceNode, key: string, label: string): TimingDetail | undefined {
  const value = timingContext[key]
  if (!isValidDuration(value)) return undefined
  return {key, label, value}
}

function getKnownDetails(timingContext: TraceNode, labels: Record<string, string>): TimingDetail[] {
  return Object.entries(labels).flatMap(([key, label]) => {
    const detail = getKnownDetail(timingContext, key, label)
    return detail ? [detail] : []
  })
}

function createTimingNode(
  detail: TimingDetail | undefined,
  key: string,
  label: string,
  children: TimingDetail[],
): TimingDetail | undefined {
  if (!detail && children.length === 0) return undefined
  return {...detail, key, label, children}
}

function getServerTimingDetails(value: unknown): Map<string, TimingDetail> {
  if (!isRecord(value)) return new Map()

  return new Map(
    Object.entries(serverTimingLabels).flatMap(([key, label]) => {
      const duration = value[key]
      return isValidDuration(duration) ? [[key, {key: `server-timing-${key}`, label, value: duration}] as const] : []
    }),
  )
}

function isValidDuration(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
}

function takeTimingDetail(details: Map<string, TimingDetail>, key: string): TimingDetail | undefined {
  const detail = details.get(key)
  details.delete(key)
  return detail
}

function takeTimingDetails(details: Map<string, TimingDetail>, keys: string[]): TimingDetail[] {
  return keys.flatMap(key => {
    const detail = takeTimingDetail(details, key)
    return detail ? [detail] : []
  })
}

function nestTimingDetail(detail: TimingDetail | undefined, children: TimingDetail[]): TimingDetail[] {
  return detail ? [{...detail, children}] : children
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
